import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LoadingIcon } from "../../components/ui/LoadingIcon";
import { getUserInformation } from "../../networking/apiMethods";
import { requireToken } from "../lib/authErrors";
import { isSessionExpired } from "./AppOverlayController";

export interface AccountContextValue {
  urlName: string | null;
  accountName: string | null;
  setAccountName: (name: string) => void;
}

export const AccountContextInstance = createContext<AccountContextValue | null>(null);

export const useAccountContext = (): AccountContextValue => {
  const ctx = useContext(AccountContextInstance);
  if (ctx === null) {
    throw new Error("useAccountContext must be used within AccountResolver");
  }
  return ctx;
};

// Routes where no account urlName is expected
const NON_ACCOUNT_PREFIXES = ["/admin", "/error", "/users"];

const isNonAccountPath = (pathname: string): boolean =>
  NON_ACCOUNT_PREFIXES.some((prefix) => pathname.startsWith(prefix));

const extractUrlNameFromPath = (pathname: string): string | null => {
  const appMatch = /^\/app\/([^/]+)/.exec(pathname);
  if (appMatch) return appMatch[1];
  // /:urlName/users is not under /app/
  const usersMatch = /^\/([^/]+)\/users/.exec(pathname);
  if (usersMatch) return usersMatch[1];
  return null;
};

/**
 * Derive the urlName synchronously from the pathname.
 * Returns null for non-account routes (e.g. /admin, /error).
 * Returns the urlName string for routes that contain a urlName segment (e.g. /app/myaccount/...).
 * Returns undefined when the path requires an async backend fetch (i.e. no urlName in URL).
 */
const deriveUrlName = (pathname: string): string | null | undefined => {
  if (isNonAccountPath(pathname)) return null;
  const fromPath = extractUrlNameFromPath(pathname);
  if (fromPath !== null) return fromPath;
  return undefined; // needs async fetch
};

interface Props {
  children: React.ReactNode;
}

export const AccountResolver: React.FC<Props> = ({ children }) => {
  const { pathname } = useLocation();
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  // Derived synchronously — undefined means "needs async fetch"
  const derivedUrlName = deriveUrlName(pathname);
  const needsFetch = derivedUrlName === undefined;

  const [accountName, setAccountName] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState(false);

  const handleSetAccountName = useCallback(
    (name: string): void => {
      setAccountName(name);
    },
    [setAccountName]
  );

  useEffect(() => {
    if (!needsFetch) return;

    let cancelled = false;

    const resolve = async (): Promise<void> => {
      try {
        const token = requireToken(await getAccessTokenSilently());
        const response = (await getUserInformation(null, token)) as {
          data: { default_url_name: string };
        };
        if (!cancelled) {
          void navigate(`/app/${response.data.default_url_name}/dashboard`, { replace: true });
        }
      } catch (err) {
        if (cancelled) return;
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          void navigate("/error", { replace: true });
        } else {
          setFetchError(true);
        }
      }
    };
    void resolve();

    return (): void => {
      cancelled = true;
    };
  }, [needsFetch, getAccessTokenSilently, navigate]);

  const contextValue = useMemo<AccountContextValue>(
    () => ({ urlName: derivedUrlName ?? null, accountName, setAccountName: handleSetAccountName }),
    [derivedUrlName, accountName, handleSetAccountName]
  );

  // Loading spinner while resolving the account — unless expired, where we defer to the overlay.
  if (needsFetch && !fetchError && !isSessionExpired()) {
    return <LoadingIcon boxWidth="100vw" boxHeight="100vh" />;
  }

  return <AccountContextInstance.Provider value={contextValue}>{children}</AccountContextInstance.Provider>;
};
